import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { MatterDetailed } from '../../../types/matters';
import { useAppStore } from '../../../store/appStore';
import { useMatters } from '../hooks/useMatters';
import { useMatterBilling } from '../hooks/useMatterBilling';
import MatterTabs from '../components/MatterTabs.tsx';
import CloseMatterModal from '../components/CloseMatterModal';

interface MatterDetailDashboardProps {
  onMatterLoaded: (matter: MatterDetailed) => void;
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isValidUuid = (value?: string | null): value is string =>
  typeof value === 'string' && UUID_PATTERN.test(value.trim());

const MatterDetailDashboard: React.FC<MatterDetailDashboardProps> = ({ onMatterLoaded }) => {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const { setSelectedMatterId } = useAppStore();
  const { fetchMatterById, loading, error: apiError } = useMatters();
  
  const [matter, setMatter] = useState<MatterDetailed | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCloseMatterModalOpen, setIsCloseMatterModalOpen] = useState(false);

  // Real-time billing stats (matter entity fields are hardcoded to 0 on backend)
  const { stats: billingStats } = useMatterBilling(matterId || '');

  // Load matter by ID when component mounts or matterId changes
  useEffect(() => {
    if (!isValidUuid(matterId)) {
      setSelectedMatterId(null);
      setMatter(null);
      setError('No valid engagement ID was provided. Return to Engagements and open the engagement again.');
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const loadMatter = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setSelectedMatterId(matterId);
        
        const foundMatter = await fetchMatterById(matterId);
        
        if (cancelled) return; // Prevent state update if component unmounted
        
        if (foundMatter) {
          setMatter(foundMatter);
          if (onMatterLoaded) {
            onMatterLoaded(foundMatter);
          }
        } else {
          setError('Engagement not found');
          setMatter(null);
        }
      } catch (err) {
        if (cancelled) return; // Prevent state update if component unmounted
        console.error('Failed to load matter:', err);
        setError('Failed to load engagement details');
        setMatter(null);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadMatter();

    return () => {
      cancelled = true; // Cancel any pending state updates
    };
  }, [matterId, fetchMatterById, setSelectedMatterId, onMatterLoaded]);

  // Removed handleMatterUpdate as it's replaced by modal-specific handlers

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
      case 'pending': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'on_hold': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400';
      case 'closed': return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Calendar fullscreen kept for MatterTabs prop compatibility but no longer toggled from header
  const handleCalendarFullscreenChange = (_isFullscreen: boolean) => {};

  // Get JWT token from Keycloak (following working pattern)
  const getAuthToken = useCallback(() => {
    console.log('🔑 [MatterDetail] Getting token from React Keycloak provider...');
    
    // 1. First try the React Keycloak provider (most reliable)
    if (keycloak?.token) {
      console.log('✅ [MatterDetail] Found token via React Keycloak provider');
      return keycloak.token;
    }
    
    // 2. Fall back to storage-based detection (same pattern as other working components)
    const jwtPattern = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
    
    console.log('🔍 [MatterDetail] Falling back to storage detection...');
    for (const storage of [sessionStorage, localStorage]) {
      for (const key of Object.keys(storage)) {
        const value = storage.getItem(key);
        if (!value) continue;
        
        if (jwtPattern.test(value)) {
          console.log(`✅ [MatterDetail] Found JWT token in ${storage === localStorage ? 'localStorage' : 'sessionStorage'}: ${key}`);
          return value;
        }
        
        if (value.startsWith('{')) {
          try {
            const parsed = JSON.parse(value);
            const tokenFields = ['token', 'access_token', 'accessToken', 'idToken', 'id_token'];
            for (const field of tokenFields) {
              if (parsed[field] && jwtPattern.test(parsed[field])) {
                console.log(`✅ [MatterDetail] Found JWT token in ${storage === localStorage ? 'localStorage' : 'sessionStorage'} ${key}.${field}`);
                return parsed[field];
              }
            }
          } catch (e) { /* ignore */ }
        }
      }
    }
    
    console.log('❌ [MatterDetail] No JWT token found');
    console.log('🔍 [MatterDetail] Keycloak authenticated:', keycloak?.authenticated);
    console.log('🔍 [MatterDetail] Keycloak instance:', !!keycloak);
    
    return '';
  }, [keycloak]);

  // Create headers with JWT authentication
  const getAuthHeaders = useCallback(() => {
    const token = getAuthToken();
    
    if (!token) {
      console.warn('🔒 [MatterDetail] No JWT token available for authentication');
    }
    
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }, [getAuthToken]);

  const handleArchiveMatter = async (data: { archive_reason: string; archive_notes: string; keep_visible: boolean }) => {
    if (!matter) return;
    
    try {
      const headers = getAuthHeaders();
      console.log('🔍 [MatterDetail] Archiving engagement with headers:', { ...headers, Authorization: headers.Authorization ? 'Bearer [REDACTED]' : 'Missing' });
      
      const response = await fetch(`/api/v1/engagements/${matter.id}/archive`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          isArchived: true,
          reason: data.archive_reason,
          notes: data.archive_notes,
          keepVisible: data.keep_visible,
        })
      });

      if (response.ok) {
        console.log('✅ [MatterDetail] Engagement archived successfully:', matter.id);
        // Redirect to engagements list after successful archive
        navigate('/engagements');
      } else {
        const errorText = await response.text();
        console.error('❌ [MatterDetail] Archive error:', response.status, errorText);
        throw new Error(`Failed to archive engagement: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Archive error:', error);
      throw error;
    }
  };

  const handleTransferMatter = async (data: { 
    to_attorney_id: string; 
    transfer_reason: string; 
    transfer_notes: string;
    client_notified: boolean;
  }) => {
    if (!matter) return;

    console.warn('Engagement transfer is not yet forked to the WeBelieve engagement API.', {
      engagementId: matter.id,
      toStaffId: data.to_attorney_id,
    });
    throw new Error('Engagement transfer is not yet forked to the WeBelieve engagement API.');
  };

  // Loading state
  if (isLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lex-accent"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading engagement details...</span>
      </div>
    );
  }

  // Error state
  if (error || apiError || !matter) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Engagement Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {error || apiError || 'The requested engagement could not be found.'}
          </p>
          <button
            onClick={() => navigate('/engagements')}
            className="px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover"
          >
            Back to Engagements
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col overflow-hidden">
      {/* Compact Header — title + status + action buttons only */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/engagements')}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {matter.displayNumber}
                </h1>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(matter.status || 'pending')}`}>
                  {matter.status ? matter.status.charAt(0).toUpperCase() + matter.status.slice(1).replace('_', ' ') : 'Pending'}
                </span>
              </div>
              <h2 className="text-sm text-gray-500 dark:text-gray-400">
                {matter.title}
              </h2>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate(`/engagements/${matter.id}/edit`)}
              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Edit
            </button>
            <button
              onClick={() => setIsCloseMatterModalOpen(true)}
              className="px-3 py-1.5 border border-red-300 dark:border-red-600 rounded-md text-sm text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              Close Engagement
            </button>
          </div>
        </div>
      </div>

      {/* Tabs — always full layout */}
      <div className="flex-1 min-h-0 px-6 pb-6 pt-4">
        <MatterTabs matter={matter} onCalendarFullscreenChange={handleCalendarFullscreenChange} />
      </div>

      {/* Close Engagement Modal */}
      {matter && (
        <CloseMatterModal
          isOpen={isCloseMatterModalOpen}
          onClose={() => setIsCloseMatterModalOpen(false)}
          matter={matter}
          onArchive={handleArchiveMatter}
          onTransfer={handleTransferMatter}
        />
      )}
    </div>
  );
};

export default MatterDetailDashboard;
